import { useEffect, useRef, useState } from 'react';
import { transformPluginsApi, TransformPluginRow } from './api/transform-plugins-client';

// FormFieldDef matches packages/shared/src/form-config/transforms/types.ts:45
interface FormFieldDef {
  key: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'lookup-table' | 'condition';
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  required?: boolean;
  helpText?: string;
}

const FIELD_TYPES: Array<{ value: FormFieldDef['type']; label: string }> = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Select' },
  { value: 'lookup-table', label: 'Lookup Table' },
  { value: 'condition', label: 'Condition' },
];

interface FormSchemaEditorProps {
  plugin: TransformPluginRow;
  onUpdate: (updated: TransformPluginRow) => void;
}

function toRows(raw: unknown): FormFieldDef[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (r): r is FormFieldDef =>
      typeof r === 'object' && r !== null && 'key' in r && 'label' in r && 'type' in r,
  );
}

export function FormSchemaEditor({ plugin, onUpdate }: FormSchemaEditorProps) {
  const [rows, setRows] = useState<FormFieldDef[]>(() => toRows(plugin.formSchema));

  // Skip the debounced save on initial render — only fire after the user
  // makes an actual edit. Mirrors IdentityPanel's pattern.
  const isFirstRender = useRef(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(async () => {
      const updated = await transformPluginsApi.update(plugin.id, { formSchema: rows });
      // eslint-disable-next-line react-hooks/exhaustive-deps
      onUpdate(updated);
    }, 800);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // onUpdate intentionally omitted — vi.fn() changes identity per render
    // and including it would re-trigger the debounce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, plugin.id]);

  function addRow() {
    setRows((prev) => [...prev, { key: '', label: '', type: 'text', required: false }]);
  }

  function removeRow(index: number) {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }

  function moveUp(index: number) {
    if (index === 0) return;
    setRows((prev) => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index]!, next[index - 1]!];
      return next;
    });
  }

  function moveDown(index: number) {
    setRows((prev) => {
      if (index >= prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1]!, next[index]!];
      return next;
    });
  }

  function updateRow(index: number, patch: Partial<FormFieldDef>) {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  // ── Select options editor (WU-6.6, closes TODO 52-followup) ──
  // Options survive a temporary type switch away from 'select' (the model
  // accepts `options` on any row) so toggling back doesn't lose work.

  function updateOptions(
    rowIndex: number,
    mutate: (opts: Array<{ value: string; label: string }>) => Array<{ value: string; label: string }>,
  ) {
    setRows((prev) =>
      prev.map((row, i) => (i === rowIndex ? { ...row, options: mutate(row.options ?? []) } : row)),
    );
  }

  function addOption(rowIndex: number) {
    updateOptions(rowIndex, (opts) => [...opts, { value: '', label: '' }]);
  }

  function removeOption(rowIndex: number, optIndex: number) {
    updateOptions(rowIndex, (opts) => opts.filter((_, j) => j !== optIndex));
  }

  function moveOption(rowIndex: number, optIndex: number, delta: -1 | 1) {
    updateOptions(rowIndex, (opts) => {
      const target = optIndex + delta;
      if (target < 0 || target >= opts.length) return opts;
      const next = [...opts];
      [next[optIndex], next[target]] = [next[target]!, next[optIndex]!];
      return next;
    });
  }

  function updateOption(
    rowIndex: number,
    optIndex: number,
    patch: Partial<{ value: string; label: string }>,
  ) {
    updateOptions(rowIndex, (opts) =>
      opts.map((opt, j) => (j === optIndex ? { ...opt, ...patch } : opt)),
    );
  }

  return (
    <div className="rb-section">
      <h2 className="rb-section-title">Form Schema</h2>

      {rows.map((row, i) => (
        <div
          key={i}
          className="rb-section"
          style={{ marginBottom: 'var(--space-3)', borderLeft: '2px solid var(--color-border)' }}
        >
          <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
            {/* Row reorder + remove controls */}
            <button
              className="rb-btn"
              aria-label="Move up"
              disabled={i === 0}
              onClick={() => moveUp(i)}
            >
              ↑
            </button>
            <button
              className="rb-btn"
              aria-label="Move down"
              disabled={i === rows.length - 1}
              onClick={() => moveDown(i)}
            >
              ↓
            </button>
            <button
              className="rb-btn rb-btn-danger"
              aria-label={`Remove ${row.key || 'field'}`}
              onClick={() => removeRow(i)}
            >
              Remove
            </button>
          </div>

          <div className="rb-row">
            <label htmlFor={`fs-key-${i}`} className="rb-label">
              Key
            </label>
            <input
              id={`fs-key-${i}`}
              className="rb-input"
              aria-label="Key"
              value={row.key}
              onChange={(e) => updateRow(i, { key: e.target.value })}
              placeholder="e.g. suffix"
            />
          </div>

          <div className="rb-row">
            <label htmlFor={`fs-label-${i}`} className="rb-label">
              Label
            </label>
            <input
              id={`fs-label-${i}`}
              className="rb-input"
              aria-label="Label"
              value={row.label}
              onChange={(e) => updateRow(i, { label: e.target.value })}
              placeholder="e.g. Suffix"
            />
          </div>

          <div className="rb-row">
            <label htmlFor={`fs-type-${i}`} className="rb-label">
              Type
            </label>
            <select
              id={`fs-type-${i}`}
              className="rb-select"
              aria-label="Type"
              value={row.type}
              onChange={(e) => updateRow(i, { type: e.target.value as FormFieldDef['type'] })}
            >
              {FIELD_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          {row.type === 'select' && (
            <div className="rb-suboptions">
              <span className="rb-label">Options</span>

              {(row.options ?? []).map((opt, j) => (
                <div key={j} className="rb-row">
                  <input
                    id={`fs-opt-${i}-${j}-value`}
                    className="rb-input"
                    aria-label="Option value"
                    value={opt.value}
                    onChange={(e) => updateOption(i, j, { value: e.target.value })}
                    placeholder="value"
                  />
                  <input
                    id={`fs-opt-${i}-${j}-label`}
                    className="rb-input"
                    aria-label="Option label"
                    value={opt.label}
                    onChange={(e) => updateOption(i, j, { label: e.target.value })}
                    placeholder="Label"
                  />
                  <button
                    className="rb-btn"
                    aria-label="Move option up"
                    disabled={j === 0}
                    onClick={() => moveOption(i, j, -1)}
                  >
                    ↑
                  </button>
                  <button
                    className="rb-btn"
                    aria-label="Move option down"
                    disabled={j === (row.options?.length ?? 0) - 1}
                    onClick={() => moveOption(i, j, 1)}
                  >
                    ↓
                  </button>
                  <button
                    className="rb-btn rb-btn-danger"
                    aria-label={`Remove option ${opt.value || j + 1}`}
                    onClick={() => removeOption(i, j)}
                  >
                    Remove
                  </button>
                </div>
              ))}

              <button className="rb-btn" onClick={() => addOption(i)}>
                + Add option
              </button>
            </div>
          )}

          <div className="rb-row">
            <label htmlFor={`fs-required-${i}`} className="rb-label">
              Required
            </label>
            <input
              id={`fs-required-${i}`}
              type="checkbox"
              aria-label="Required"
              checked={row.required ?? false}
              onChange={(e) => updateRow(i, { required: e.target.checked })}
            />
          </div>

          <div className="rb-row">
            <label htmlFor={`fs-helptext-${i}`} className="rb-label">
              Help Text
            </label>
            <input
              id={`fs-helptext-${i}`}
              className="rb-input"
              aria-label="Help Text"
              value={row.helpText ?? ''}
              onChange={(e) => updateRow(i, { helpText: e.target.value })}
              placeholder="Optional helper text shown below the field"
            />
          </div>
        </div>
      ))}

      <div className="rb-footer">
        <button className="rb-btn rb-btn-save" onClick={addRow}>
          + Add field
        </button>
      </div>
    </div>
  );
}
